import React, { useState, useEffect } from 'react';
import { Alert, AlertTitle, Button, Box, Chip } from '@mui/material';
import api from '../services/api';

interface ReverificationItem {
  id: number;
  name: string;
  is_active: boolean;
  status: string; // "current" | "due_soon" | "overdue" | "paused" | "not_set"
  verification_due_at: string | null;
  paused_for_reverification: boolean;
}

const ReverificationBanner: React.FC = () => {
  const [items, setItems] = useState<ReverificationItem[]>([]);
  const [reverifying, setReverifying] = useState<number | null>(null);

  const fetchStatus = async () => {
    try {
      const res = await api.get('/api/v1/strategies/reverification-status');
      const actionNeeded = (res.data || []).filter(
        (s: ReverificationItem) => s.status === 'due_soon' || s.status === 'overdue' || s.status === 'paused'
      );
      setItems(actionNeeded);
    } catch {
      // Silently fail
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleReverify = async (strategyId: number) => {
    try {
      setReverifying(strategyId);
      await api.post(`/api/v1/strategies/${strategyId}/reverify`);
      await fetchStatus();
    } catch {
      // Silently fail
    } finally {
      setReverifying(null);
    }
  };

  if (items.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      {items.map((item) => (
        <Alert
          key={item.id}
          severity={item.status === 'paused' || item.status === 'overdue' ? 'error' : 'warning'}
          sx={{ mb: 1 }}
          action={
            <Button
              color="inherit"
              size="small"
              disabled={reverifying === item.id}
              onClick={() => handleReverify(item.id)}
            >
              {reverifying === item.id ? 'Verifying...' : 'Re-verify Now'}
            </Button>
          }
        >
          <AlertTitle>
            {item.name}
            <Chip
              label={item.status === 'paused' ? 'Paused' : item.status === 'overdue' ? 'Overdue' : 'Due Soon'}
              size="small"
              sx={{
                ml: 1,
                backgroundColor: item.status === 'paused' || item.status === 'overdue' ? '#C0392B' : '#E8A045',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.7rem',
              }}
            />
          </AlertTitle>
          {item.status === 'paused'
            ? 'This strategy has been paused because it was not re-verified within 30 days. Re-verify to reactivate.'
            : item.status === 'overdue'
            ? 'This strategy is overdue for re-verification and will be paused soon.'
            : `Re-verification due by ${item.verification_due_at ? new Date(item.verification_due_at).toLocaleDateString() : 'soon'}.`}
        </Alert>
      ))}
    </Box>
  );
};

export default ReverificationBanner;
